import React, {ReactNode} from 'react'
import {Nullable} from '../../../app/TypeHelpers'
import {
    ACCESS_CONTROL_MANAGER,
    CLEANING_TIME,
    FACILITY_CATEGORY,
    PUBLISHING_SETTING,
    RELEASE_START_HOURS,
    RENTAL_TYPE,
    RESERVATION_TIME_UNIT_MINUTES,
} from '../../../CodeMaster'
import PPSelect, {OptionProps} from '../../shared/forms/PPSelect'
import PPImageInputWithPreview, {ImageInformation} from '../../shared/forms/PPImageInputWithPreview'
import ReservationSettingsComponent, {ReservationSettings} from './ReservationSettingsComponent'
import {OptionSetting} from './FacilityOptionSetting/FacilityOptionInputForm'
import StaySettingsComponent, {StaySettings} from './StaySettingsComponent'
import {TimeTableItem} from './BusinessScheduleSetting/BusinessScheduleItemInterfaces'
import {
    MaxReservationTimeByTimeZone,
    MaxReservationTimeInputFormProps,
} from './BusinessScheduleSetting/MaxReservationTimeInputForm/MaxReservationTimeInputForm'
import {numberOrNull, stringOrEmpty} from '../../../stringHelper/stringHelpers'
import {Messages} from '../../../Messages'
import {EquipmentsRepository} from '../../equipments/EquipmentsRepository'
import FacilitiesRepository from '../FacilitiesRepository'
import FacilityModel, {
    FacilityModelDataSet,
    FacilityModelInitializeObserver,
    NewFacilityModelObserver,
    UpdateFacilityModelObserver,
} from '../FacilityModel'
import {StyledTableCaption, TableRowName} from '../../../styledComponents/adminPage/Table'
import PPNumericField from '../../shared/forms/PPNumericField'
import {HKConstants} from '../../../HKConstants'
import {addEmptyData, isNoSelect, valueOrNoSelected} from '../../../componentHelper/selectHelper'
import EquipmentsSelector from './EquipmentsSelector'
import PPTextField from '../../shared/forms/PPTextField'
import PPHtmlEditor from '../../shared/forms/PPHtmlEditor'
import PPRadioGroup, {PPRadioItemProp} from '../../shared/forms/PPRadioGroup'
import BusinessScheduleSetting from './BusinessScheduleSetting/BusinessScheduleSetting'
import {EquipmentSimple} from '../../equipments/EquipmentSimple'
import {AsyncCommunicationProps} from '../../shared/hoc/withAsyncCommunication'
import {LoggerProps} from '../../shared/hoc/withLogger'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import equal from 'fast-deep-equal'
import FacilityDetails from '../FacilityDetails'
import styled from 'styled-components'
import {DialogComponent} from '../../shared/pageBaseComponents/DialogComponent'
import {convertToMoment} from '../../reservations/helpers/helpers'
import {LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import PPDatePicker from '../../shared/forms/PPDatePicker'
import moment from 'moment'
import PPErrorLabel from '../../shared/forms/PPErrorLabel'
import NatCondominiumRepo from '../../../__NoAutomatedTests/components/condominium/repository/NatCondominiumRepo'
import {NatGetCondominium} from '../../../__NoAutomatedTests/components/condominium/repository/NatGetCondominium'
import {SelectChangeEvent} from '@mui/material/Select'
import {Box, Table, TableBody, TableCell, TableRow} from '@mui/material'
import {CardKeyFacilityNumberMask} from './CardKeyFacilityNumberMask'
import ReactDOM from 'react-dom'
import GateNumbersForm from './GateNumbersForm'

interface Props {
    facilityId: Nullable<number>
    condominiumCd: string
    natCondominiumRepo: NatCondominiumRepo
    equipmentsRepo: EquipmentsRepository
    facilitiesRepo: FacilitiesRepository
    facilityModel: FacilityModel
    newFacilityModelObserver?: NewFacilityModelObserver
    updateFacilityModelObserver?: UpdateFacilityModelObserver
    eventEmitter: EventEmitterExAdmin
}

export interface State {
    isLoadCompleted: boolean
    equipmentOptionsMaster: OptionProps[]
    sortOrder: Nullable<number>
    facilityCategory: Nullable<FACILITY_CATEGORY>
    equipmentIds: number[]
    facilityName: string
    accessControlManager: Nullable<ACCESS_CONTROL_MANAGER>
    cardKeyFacilityNumber: Nullable<string>
    gateNumbers: string[]
    images: ImageInformation[]
    reservationSettings: ReservationSettings
    releaseSetting: PUBLISHING_SETTING
    releaseStartDate: Nullable<string>
    releaseStartTime: Nullable<RELEASE_START_HOURS>
    facilityIntroductory: string
    optionSettings: OptionSetting[]
    reserveBeforeCleaningTime: Nullable<CLEANING_TIME>
    reserveAfterCleaningTime: Nullable<CLEANING_TIME>
    stayAvailability: RENTAL_TYPE
    staySettings: StaySettings
    maxReservationTimeSetting: MaxReservationTimeInputFormProps
    timeTableItems: TimeTableItem[]
    stayAvailabilityDialog: boolean
    changedStayAvailability: RENTAL_TYPE
    condominiumAccessControlManagerList: string[]
}

class FacilityForm extends React.Component<Props & AsyncCommunicationProps & LoggerProps, State>
    implements FacilityModelInitializeObserver {
    constructor(props: Readonly<Props & AsyncCommunicationProps & LoggerProps>) {
        super(props)
        this.state = {
            ...this.props.facilityModel.getDataSet(),

            isLoadCompleted: false,
            equipmentOptionsMaster: [],
            stayAvailabilityDialog: false,
            changedStayAvailability: RENTAL_TYPE.OVERNIGHT,
            condominiumAccessControlManagerList: [],
        }

        this.handleSortOrderChange = this.handleSortOrderChange.bind(this)
        this.handleEquipmentsChange = this.handleEquipmentsChange.bind(this)
        this.handleFacilityCategoryChange = this.handleFacilityCategoryChange.bind(this)
        this.handleFacilityNameChange = this.handleFacilityNameChange.bind(this)
        this.handleChangeAccessControlManager = this.handleChangeAccessControlManager.bind(this)
        this.handleCardKeyFacilityNumberChange = this.handleCardKeyFacilityNumberChange.bind(this)
        this.handleGateNumbersChange = this.handleGateNumbersChange.bind(this)
        this.handleImagesChange = this.handleImagesChange.bind(this)
        this.handleStaySettingsChange = this.handleStaySettingsChange.bind(this)
        this.handleReservationSettingsChange = this.handleReservationSettingsChange.bind(this)
        this.handleFacilityIntroductoryChange = this.handleFacilityIntroductoryChange.bind(this)
        this.handleUpdateOptionSettings = this.handleUpdateOptionSettings.bind(this)
        this.handleReleaseSettingChange = this.handleReleaseSettingChange.bind(this)
        this.handleReserveBeforeCleaningTimeChange = this.handleReserveBeforeCleaningTimeChange.bind(this)
        this.handleReserveAfterCleaningTimeChange = this.handleReserveAfterCleaningTimeChange.bind(this)
        this.handleBusinessScheduleChange = this.handleBusinessScheduleChange.bind(this)
        this.handleChangeIsMultipleMaxReservationTime = this.handleChangeIsMultipleMaxReservationTime.bind(this)
        this.handleChangeSingleMaxReservationTime = this.handleChangeSingleMaxReservationTime.bind(this)
        this.handleChangeMultipleMaxReservationTimes = this.handleChangeMultipleMaxReservationTimes.bind(this)
        this.handleChangeMultipleMaxReservationUnitMinute = this.handleChangeMultipleMaxReservationUnitMinute.bind(this)
        this.handleChangeStayAvailability = this.handleChangeStayAvailability.bind(this)
        this.handleOptionClearOkClick = this.handleOptionClearOkClick.bind(this)
        this.handleOptionClearCancelClick = this.handleOptionClearCancelClick.bind(this)
        this.handleChangeReleaseStartDate = this.handleChangeReleaseStartDate.bind(this)
        this.handleChangeReleaseStartTime = this.handleChangeReleaseStartTime.bind(this)
    }

    shouldComponentUpdate(
        nextProps: Readonly<Props & AsyncCommunicationProps & LoggerProps>, nextState: Readonly<State>,
    ): boolean {
        return !(
            (this.props === nextProps || equal(this.props, nextProps))
            && (this.state === nextState || equal(this.state, nextState))
        )
    }

    async componentDidMount(): Promise<void> {
        this.props.log.debug('FacilityForm.componentDidMount() start')
        const {eventEmitter} = this.props

        let succeed: boolean = false
        try {
            await eventEmitter.showLoading()

            const condominium: Nullable<NatGetCondominium> =
                await this.props.asyncCallFunction(
                    this.props.natCondominiumRepo.getCondominium.bind(
                        this.props.natCondominiumRepo, this.props.condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )
            if (!condominium || !condominium.details) {
                return
            }
            ReactDOM.flushSync(() => {
                this.setState({
                    condominiumAccessControlManagerList: condominium.details!.accessControlManagerList,
                })
            })

            succeed = await this.setupEquipmentsMaster()
            if (succeed) {
                succeed = await this.setupInitialData()
            }
        } finally {
            eventEmitter.closeLoading()
            this.setState({isLoadCompleted: succeed})
        }
    }

    async setupEquipmentsMaster(): Promise<boolean> {
        const {equipmentsRepo, eventEmitter, condominiumCd} = this.props
        const equipments: EquipmentSimple[] | null =
            await this.props.asyncCallFunction(
                equipmentsRepo.getEquipmentsForFacility.bind(equipmentsRepo, condominiumCd),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
        if (!equipments) {
            return false
        }
        ReactDOM.flushSync(() => {
            this.setState({
                equipmentOptionsMaster: equipments.map((equipment) => {
                    return {label: equipment.name, value: `${equipment.id}`}
                }),
            })
        })
        return true
    }

    async setupInitialData(): Promise<boolean> {
        const {
            facilitiesRepo,
            condominiumCd,
            facilityId,
            eventEmitter,
            newFacilityModelObserver,
            updateFacilityModelObserver,
            facilityModel,
        } = this.props

        let serverData: FacilityDetails | null = null
        if (facilityId !== null) {
            serverData = await this.props.asyncCallFunction(
                facilitiesRepo.getFacilityDetails.bind(
                    facilitiesRepo,
                    condominiumCd,
                    facilityId,
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
            if (serverData === null) {
                return false
            }
        }

        facilityModel.reset()
        if (newFacilityModelObserver) {
            facilityModel.addNewFacilityModelObserver(newFacilityModelObserver)
        }
        if (updateFacilityModelObserver) {
            facilityModel.addUpdateFacilityModelObserver(updateFacilityModelObserver)
        }
        facilityModel
            .addFacilityModelInitializeObserver(this)
            .initialize(serverData)

        return true
    }

    onInitialize(initData: FacilityModelDataSet): void {
        this.setState({...this.state, ...initData})
    }

    handleSortOrderChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const sortOrder: Nullable<number> = numberOrNull(event.target.value)
        this.setState({sortOrder}, () => {
            this.props.facilityModel.withSortOrder(sortOrder)
        })
    }

    handleFacilityCategoryChange(event: SelectChangeEvent<unknown>): void {
        const value: string = `${event.target.value}`
        const facilityCategory: Nullable<FACILITY_CATEGORY> = isNoSelect(value) ? null :
            FACILITY_CATEGORY.codeToEnumConstants(value)
        this.setState({facilityCategory: facilityCategory}, () => {
            this.props.facilityModel.withFacilityCategory(facilityCategory)
        })
    }

    handleEquipmentsChange(selectedEquipmentIds: number[]): void {
        this.setState({equipmentIds: selectedEquipmentIds}, () => {
            this.props.facilityModel.withEquipmentIds(selectedEquipmentIds)
        })
    }

    handleReservationSettingsChange(reservationSettings: ReservationSettings): void {
        this.setState({reservationSettings}, () => {
            this.props.facilityModel.withReservationSettings(reservationSettings)
        })
    }

    handleFacilityNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const facilityName: string = event.target.value
        this.setState({facilityName}, () => {
            this.props.facilityModel.withFacilityName(facilityName)
        })
    }

    handleChangeAccessControlManager(event: React.ChangeEvent<HTMLInputElement>): void {
        const accessControlManager: Nullable<ACCESS_CONTROL_MANAGER> = `${event.target.value}` === '0'
            ? null
            : ACCESS_CONTROL_MANAGER.codeToEnumConstants(`${event.target.value}`)
        this.setState({accessControlManager}, () => {
            this.props.facilityModel.withAccessControlManager(accessControlManager)
        })
    }

    handleCardKeyFacilityNumberChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const cardKeyFacilityNumber: string = event.target.value
        this.setState({cardKeyFacilityNumber}, () => {
            const defaultBlankValue: string = '    -  '
            if (cardKeyFacilityNumber !== defaultBlankValue) {
                this.props.facilityModel.withCardKeyFacilityNumber(cardKeyFacilityNumber)
            } else {
                this.props.facilityModel.withCardKeyFacilityNumber(null)
            }
        })
    }

    handleGateNumbersChange(gateNumbers: string[]): void {
        this.setState({gateNumbers}, () => {
            this.props.facilityModel.withGateNumbers(gateNumbers)
        })
    }

    handleImagesChange(images: ImageInformation[]): void {
        this.setState({images}, () => {
            this.props.facilityModel.withImages(images)
        })
    }

    handleStaySettingsChange(staySettings: StaySettings): void {
        this.setState({
            staySettings: staySettings,
        }, () => {
            this.props.facilityModel.withStaySettings(staySettings)
        })
    }

    handleFacilityIntroductoryChange(facilityIntroductory: string): void {
        this.setState({facilityIntroductory}, () => {
            this.props.facilityModel.withFacilityIntroductory(facilityIntroductory)
        })
    }

    handleUpdateOptionSettings(optionSettings: OptionSetting[]): void {
        this.setState({optionSettings}, () => {
            this.props.facilityModel.withOptionSettings(optionSettings)
        })
    }

    handleReleaseSettingChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const releaseSetting: PUBLISHING_SETTING = PUBLISHING_SETTING.codeToEnumConstants(`${event.target.value}`)
        this.setState({releaseSetting}, () => {
            this.props.facilityModel.withReleaseSetting(releaseSetting)
        })
    }

    handleChangeReleaseStartDate(date: Nullable<Date>): void {
        let releaseStartDate: Nullable<string> = null
        if (date !== null) {
            releaseStartDate = moment(date.toISOString()).format('YYYY/MM/DD')
        }

        this.setState({releaseStartDate}, () => {
            this.props.facilityModel.withReleaseStartDate(releaseStartDate)
        })
    }

    handleChangeReleaseStartTime(event: SelectChangeEvent<unknown>): void {
        let startTime: Nullable<string> = null
        if (event.target.value !== undefined && event.target.value !== null && !isNoSelect(event.target.value)) {
            startTime = `${event.target.value}`
        }

        const releaseStartTime: Nullable<RELEASE_START_HOURS> = startTime === null ?
            null : RELEASE_START_HOURS.codeToEnumConstants(startTime)
        this.setState({releaseStartTime: releaseStartTime}, () => {
            this.props.facilityModel.withReleaseStartTime(releaseStartTime)
        })
    }

    handleReserveBeforeCleaningTimeChange(cleaningTime: Nullable<CLEANING_TIME>): void {
        this.setState({reserveBeforeCleaningTime: cleaningTime})
        this.props.facilityModel.withReserveBeforeCleaningTime(cleaningTime)
    }

    handleReserveAfterCleaningTimeChange(cleaningTime: Nullable<CLEANING_TIME>): void {
        this.setState({reserveAfterCleaningTime: cleaningTime})
        this.props.facilityModel.withReserveAfterCleaningTime(cleaningTime)
    }

    handleBusinessScheduleChange(timeTableItems: TimeTableItem[]): void {
        this.setState(
            {timeTableItems: timeTableItems},
            () => this.props.facilityModel.withTimeTableItems(this.state.timeTableItems),
        )
    }

    handleChangeIsMultipleMaxReservationTime(isMultipleMaxReservationTime: boolean): void {
        this.setState((prevState: State) => {
            return {
                maxReservationTimeSetting: {
                    ...prevState.maxReservationTimeSetting,
                    isMultipleMaxReservationTime: isMultipleMaxReservationTime,
                },
            }
        }, () => {
            return (this.props.facilityModel.withMaxReservationTimeSetting(
                this.state.maxReservationTimeSetting,
            ))
        })
    }

    handleChangeSingleMaxReservationTime(time: Nullable<number>): void {
        this.setState((prevState: State) => {
            return {
                maxReservationTimeSetting: {
                    ...prevState.maxReservationTimeSetting,
                    singleMaxReservationTime: time,
                },
            }
        }, () => {
            return (this.props.facilityModel.withMaxReservationTimeSetting(
                this.state.maxReservationTimeSetting,
            ))
        })
    }

    handleChangeMultipleMaxReservationTimes(timezones: MaxReservationTimeByTimeZone[]): void {
        this.setState((prevState: State) => {
            return {
                maxReservationTimeSetting: {
                    ...prevState.maxReservationTimeSetting,
                    multipleMaxReservationTimes: timezones,
                },
            }
        }, () => {
            return (this.props.facilityModel.withMaxReservationTimeSetting(
                this.state.maxReservationTimeSetting,
            ))
        })
    }

    handleChangeMultipleMaxReservationUnitMinute(minute: Nullable<RESERVATION_TIME_UNIT_MINUTES>): void {
        this.setState((prevState: State) => {
            return {
                maxReservationTimeSetting: {
                    ...prevState.maxReservationTimeSetting,
                    multipleMaxReservationUnitMinute: minute,
                },
            }
        }, () => {
            return (this.props.facilityModel.withMaxReservationTimeSetting(
                this.state.maxReservationTimeSetting,
            ))
        })
    }

    handleChangeStayAvailability(
        event: React.ChangeEvent<{ name?: string; value: unknown }>,
    ): void {
        const stayAvailability: RENTAL_TYPE =
            RENTAL_TYPE.codeToEnumConstants(`${event.target.value}`)

        if (stayAvailability !== this.state.stayAvailability) {
            if (this.state.optionSettings.length > 0) {
                this.setState({stayAvailabilityDialog: true, changedStayAvailability: stayAvailability})
            } else {
                this.setState({
                    stayAvailability: stayAvailability,
                }, () => {
                    this.props.facilityModel.withStayAvailability(stayAvailability)
                })

            }
        }
    }

    handleOptionClearCancelClick(): void {
        this.setState({stayAvailabilityDialog: false})
    }

    handleOptionClearOkClick(
        stayAvailability: RENTAL_TYPE,
    ): void {
        let needToClearOptionSettings: boolean = false


        this.setState((prevState) => {
            const prevStayAvailable: boolean =
                (prevState && prevState.stayAvailability === RENTAL_TYPE.OVERNIGHT)
            const currentStayAvailable: boolean = (stayAvailability === RENTAL_TYPE.OVERNIGHT)
            needToClearOptionSettings = (prevStayAvailable !== currentStayAvailable)

            return ({stayAvailability, stayAvailabilityDialog: false})
        }, () => {
            if (needToClearOptionSettings) {
                this.handleUpdateOptionSettings([])
            }

            this.props.facilityModel.withStayAvailability(stayAvailability)
        })

    }

    renderStayAvailabilityDialog(): ReactNode {
        return (
            <DialogComponent
                params={
                    {
                        title: '確認',
                        errorLevel: Messages.IFACA0037.errorLevel,
                        message: Messages.IFACA0037.toString(),
                        buttonProps: [
                            {
                                isDefault: false,
                                buttonLabel: 'キャンセル',
                                buttonHandler: this.handleOptionClearCancelClick,
                            },
                            {
                                isDefault: false,
                                buttonLabel: 'OK',
                                buttonHandler: () => this.handleOptionClearOkClick(this.state.changedStayAvailability),
                            },
                        ],
                    }
                }
                open={this.state.stayAvailabilityDialog}
            ></DialogComponent>
        )
    }

    renderReleaseStartDateTime(): ReactNode {
        if (this.state.releaseSetting === PUBLISHING_SETTING.PUBLIC) {
            return null
        }

        let releaseStartDate: Nullable<Date> = null
        if (this.state.releaseStartDate !== null) {
            releaseStartDate = convertToMoment(this.state.releaseStartDate, null).toDate()
        }
        const releaseStartTime: Nullable<RELEASE_START_HOURS> = this.state.releaseStartTime

        return (<>
            <Box marginRight={'30px'}/>
            <label>
                公開開始日時
            </label>
            <Box marginRight={'30px'}/>
            <PPDatePicker
                value={releaseStartDate}
                eventEmitter={this.props.eventEmitter}
                eventKeys={['releaseStartDate']}
                onChangeDate={this.handleChangeReleaseStartDate}
            />
            <Box marginRight={'30px'}/>
            <PPSelect
                data={addEmptyData(RELEASE_START_HOURS.values())}
                value={valueOrNoSelected(releaseStartTime)}
                eventEmitter={this.props.eventEmitter}
                eventKeys={['releaseStartTime']}
                onChange={this.handleChangeReleaseStartTime}
            />
            <span>時</span>
            <Box marginRight={'30px'}/>
            <PPErrorLabel
                eventEmitter={this.props.eventEmitter}
                eventKeys={['releaseStartDateTime']}
            />
        </>)
    }

    render(): ReactNode {
        if (!this.state.isLoadCompleted) {
            return null
        }
        const shouldDisplayBusinessScheduleSetting: boolean = (
            (this.state.staySettings !== null)
            && (this.state.stayAvailability === RENTAL_TYPE.DURATION)
        )

        const accessControlManagerList: PPRadioItemProp[] = [
            {
                value: '0',
                label: 'なし',
            },
        ]
        ACCESS_CONTROL_MANAGER.values().forEach(accessControlManager =>
            accessControlManagerList.push({
                ...accessControlManager,
                radioProps: {
                    disabled: !this.state.condominiumAccessControlManagerList.includes(accessControlManager.value),
                },
            }),
        )

        return (<form>
            <Table>
                <StyledTableCaption>施設情報</StyledTableCaption>
                <TableBody>
                    <TableRow>
                        <TableRowName>並び順</TableRowName>
                        <TableCell>
                            <SortOrderInput
                                InputProps={{
                                    inputProps: {
                                        min: 1,
                                        max: HKConstants.VMN.FAC.MAX_ORDER_OF_FACILITY_LIST_DISPLAY,
                                    },
                                }}
                                placeholder={`${HKConstants.VMN.FAC.MAX_ORDER_OF_FACILITY_LIST_DISPLAY}`}
                                value={stringOrEmpty(this.state.sortOrder)}
                                onChange={this.handleSortOrderChange}
                                eventEmitter={this.props.eventEmitter}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>カテゴリ</TableRowName>
                        <TableCell>
                            <PPSelect
                                required
                                data={addEmptyData(FACILITY_CATEGORY.values(), 'カテゴリを選択してください')}
                                value={valueOrNoSelected(this.state.facilityCategory)}
                                onChange={this.handleFacilityCategoryChange}
                                eventEmitter={this.props.eventEmitter}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>設備</TableRowName>
                        <TableCell>
                            <EquipmentsSelector
                                required
                                masterData={this.state.equipmentOptionsMaster}
                                selectedIdList={this.state.equipmentIds}
                                eventEmitter={this.props.eventEmitter}
                                onChange={this.handleEquipmentsChange}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>施設名</TableRowName>
                        <TableCell>
                            <PPTextField
                                InputProps={{
                                    inputProps: {
                                        maxLength: HKConstants.VMN.FAC.MAX_LENGTH_OF_FACILITY_NAME,
                                    },
                                }}
                                fullWidth
                                value={this.state.facilityName}
                                eventEmitter={this.props.eventEmitter}
                                eventKeys={['facilityName']}
                                onChange={this.handleFacilityNameChange}
                            />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName required>入退室管理</TableRowName>
                        <TableCell>
                            <PPRadioGroup
                                row
                                data={accessControlManagerList}
                                value={this.state.accessControlManager?.value ?? '0'}
                                onChange={this.handleChangeAccessControlManager}
                                eventKeys={['accessControlManager']}
                                eventEmitter={this.props.eventEmitter}
                            />
                            {this.state.accessControlManager === ACCESS_CONTROL_MANAGER.ODEC_TECHNO && <>
                                <Box height={'10px'}/>
                                <CardKeyFacilityNumberLabel>
                                    カードキー施設番号
                                    <Box width={'20px'}/>
                                    <CardKeyFacilityNumber
                                        InputProps={{
                                            inputComponent: CardKeyFacilityNumberMask as any,
                                        }}
                                        value={this.state.cardKeyFacilityNumber}
                                        hint={Messages.HFACA0001.toString()}
                                        eventEmitter={this.props.eventEmitter}
                                        eventKeys={['cardKeyFacilityNumber']}
                                        onChange={this.handleCardKeyFacilityNumberChange}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled': {
                                                backgroundColor: '#DDD',
                                            },
                                        }}
                                    />
                                </CardKeyFacilityNumberLabel>
                            </>}
                            {this.state.accessControlManager === ACCESS_CONTROL_MANAGER.TEBRA_PASS && <>
                                <Box height={'10px'}/>
                                <GateNumbersForm
                                    gateNumbers={this.state.gateNumbers}
                                    eventEmitter={this.props.eventEmitter}
                                    onChange={this.handleGateNumbersChange}
                                />
                            </>}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableRowName>施設紹介文</TableRowName>
                        <TableCell>
                            <PPHtmlEditor
                                init={{
                                    height: '300px',
                                }}
                                value={this.state.facilityIntroductory}
                                eventEmitter={this.props.eventEmitter}
                                eventKeys={['facilityIntroductory']}
                                onEditorChange={this.handleFacilityIntroductoryChange}
                            />
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableRowName>画像</TableRowName>
                        <TableCell>
                            <PPImageInputWithPreview
                                images={this.state.images}
                                maxCount={4}
                                maxFileSizeBytesPerFile={5 * 1024 * 1024}
                                maxFileSizeBytesPerFileMessage={
                                    'アップロードファイルの上限サイズは1ファイルあたり5MBまでです'
                                }
                                helperText={<div>5MBまで</div>}
                                eventEmitter={this.props.eventEmitter}
                                inputProps={{
                                    accept: 'image/jpeg,image/png,image/gif',
                                }}
                                eventKeys={['invalidImage']}
                                onChange={this.handleImagesChange}
                            />
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableRowName>公開状態</TableRowName>
                        <TableCell>
                            <LeftRowFlexContainer>
                                <PPRadioGroup
                                    row
                                    data={PUBLISHING_SETTING.values()}
                                    value={this.state.releaseSetting.value}
                                    onChange={this.handleReleaseSettingChange}
                                />
                                {this.renderReleaseStartDateTime()}
                            </LeftRowFlexContainer>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <Table>
                <StyledTableCaption>予約設定</StyledTableCaption>
                <TableBody>
                    <ReservationSettingsComponent
                        {...this.state.reservationSettings}
                        eventEmitter={this.props.eventEmitter}
                        handleReservationSettingsChange={this.handleReservationSettingsChange}
                    />

                    <TableRow>
                        <TableRowName required>貸出方法</TableRowName>
                        <TableCell>
                            <PPRadioGroup
                                row
                                data={RENTAL_TYPE.values()}
                                value={this.state.stayAvailability.value}
                                onChange={this.handleChangeStayAvailability}
                            />
                        </TableCell>
                    </TableRow>

                </TableBody>
            </Table>

            {this.state.stayAvailability === RENTAL_TYPE.OVERNIGHT &&
                <StaySettingsComponent
                    stayAvailability={this.state.stayAvailability}
                    stayPossibleDateCount={this.state.staySettings.stayPossibleDateCount}
                    stayContinueTimeRental={this.state.staySettings.stayContinueTimeRental}
                    checkinTimeHours={this.state.staySettings.checkinTimeHours}
                    checkinTimeMinutes={this.state.staySettings.checkinTimeMinutes}
                    checkoutTimeHours={this.state.staySettings.checkoutTimeHours}
                    checkoutTimeMinutes={this.state.staySettings.checkoutTimeMinutes}
                    stayCharge={this.state.staySettings.stayCharge}
                    eventEmitter={this.props.eventEmitter}
                    handleStaySettingsChange={this.handleStaySettingsChange}
                    optionSettings={this.state.optionSettings}
                    reserveBeforeCleaningTime={this.state.reserveBeforeCleaningTime}
                    reserveAfterCleaningTime={this.state.reserveAfterCleaningTime}
                    onUpdateOptions={this.handleUpdateOptionSettings}
                    onBeforeCleaningTimeChange={this.handleReserveBeforeCleaningTimeChange}
                    onAfterCleaningTimeChange={this.handleReserveAfterCleaningTimeChange}
                />
            }
            {
                shouldDisplayBusinessScheduleSetting &&
                <BusinessScheduleSetting
                    items={this.state.timeTableItems}
                    cleaningSetting={{
                        cleaningTimeBeforeReservation: this.state.reserveBeforeCleaningTime,
                        cleaningTimeAfterReservation: this.state.reserveAfterCleaningTime,
                    }}
                    maxReservationTimeSetting={{
                        ...this.state.maxReservationTimeSetting,
                        onChangeIsMultipleMaxReservationTime: this.handleChangeIsMultipleMaxReservationTime,
                        onChangeSingleMaxReservationTime: this.handleChangeSingleMaxReservationTime,
                        onChangeMultipleMaxReservationTimes: this.handleChangeMultipleMaxReservationTimes,
                        onChangeMultipleMaxReservationUnitMinute: this.handleChangeMultipleMaxReservationUnitMinute,
                    }}
                    onChangeBusinessSchedule={this.handleBusinessScheduleChange}
                    stayAvailability={this.state.stayAvailability}
                    optionSettings={this.state.optionSettings}
                    reserveBeforeCleaningTime={this.state.reserveBeforeCleaningTime}
                    reserveAfterCleaningTime={this.state.reserveAfterCleaningTime}
                    onUpdateOptions={this.handleUpdateOptionSettings}
                    onBeforeCleaningTimeChange={this.handleReserveBeforeCleaningTimeChange}
                    onAfterCleaningTimeChange={this.handleReserveAfterCleaningTimeChange}
                    eventEmitter={this.props.eventEmitter}
                />
            }
            {this.renderStayAvailabilityDialog()}
        </form>)

    }
}

const SortOrderInput = styled(PPNumericField)`
  width: 60px;
`

const CardKeyFacilityNumber = styled(PPTextField)`
  width: 90px;
`

const CardKeyFacilityNumberLabel = styled.label`
  display: flex;
  align-items: center;
`

export default FacilityForm
